import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import { createEntityAdapter, EntityState } from '@reduxjs/toolkit';

import createSafeContext from '#/utils/createSafeContext';
import { useApi } from '#/api/fetch-api';
import { getMarkets, OptionMarket } from '#/api/markets';
import * as OptionFn from '#/features/options/functions';

const selectId = (market: OptionMarket) => market.symbol;

// WIP: use `sortComparer` to sort by expiry date, closest expiry date at index 0
//      this will affect all the downstream contexts, e.g. we now have issue with earliest expiry not being first in `markets-routing-context`
const adapter = createEntityAdapter<OptionMarket>({ selectId });

const INITIAL_STATE: MarketsState = {
  status: 'loading',
  error: '',
  markets: adapter.getInitialState(),
};

export interface MarketsState {
  readonly status: 'loading' | 'error' | 'done';
  readonly error: string;
  readonly markets: EntityState<OptionMarket>;
}

export const [useOptionMarketsState, MarketsStateCtx] =
  createSafeContext<MarketsState>('MarketsState');

interface ProviderProps {
  readonly children: ReactNode;
}

export function OptionMarketsStateProvider(props: ProviderProps) {
  const [state, setState] = useState(INITIAL_STATE);

  useMarketsFetch(setState);

  return <MarketsStateCtx value={state}>{props.children}</MarketsStateCtx>;
}

function useMarketsFetch(
  setState: Dispatch<SetStateAction<MarketsState>>,
): void {
  const [, fetchMarkets] = useApi(getMarkets);

  useEffect(() => {
    setState((state) => ({
      ...state,
      status: 'loading',
      error: '',
    }));

    fetchMarkets({}, (resp) => {
      if (!resp.ok) {
        setState((state) => ({
          ...state,
          status: 'error',
          error: resp.message,
        }));
        return;
      }

      const marketResults = resp.data.results.filter(OptionFn.isOptionMarket);

      setState((state) => {
        const optionMarkets = adapter.setAll(state.markets, marketResults);

        return {
          status: 'done',
          error: '',
          markets: optionMarkets,
        };
      });
    });
  }, [fetchMarkets, setState]);
}
